import { Box, styled } from "@mui/material";
import { StoneType } from "../models";
import React from "react";

/**
 * Default Stone Sets - Hochwertige, nicht-icon-basierte Spielsteine
 *
 * Jedes Theme hat sein eigenes Stone-Set mit:
 * - 4 Attribute: Color (0/1), Size (0/1), Shape (0/1), Variant (0/1)
 * - Klar unterscheidbar
 * - Premium Design
 */

interface DefaultStoneProps {
  stone: StoneType;
  themeSet: "classic" | "modern" | "dark";
}

// ==================== CLASSIC THEME ====================
// Holz-Style: Warm, traditionell, natürlich
// 4 Eigenschaften:
// - COLOR: Hell (Maple) vs Dunkel (Walnut)
// - SIZE: Klein (60%) vs Groß (130%)
// - SHAPE: Rund vs Eckig
// - VARIANT: Voll OHNE Gravur vs Hohl MIT Gravur

const ClassicStoneWrapper = styled(Box)<{ $variant: number; $size: number }>(
  ({ theme, $variant, $size }) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    // SIZE: Much more visible difference!
    // Small: 60% size, Large: 130% size
    transform: $size === 1 ? "scale(1.3)" : "scale(0.6)",
  })
);

const ClassicStoneBase = styled(Box)<{
  $color: number;
  $shape: number;
  $variant: number;
}>(({ theme, $color, $shape, $variant }) => {
  // Realistic wood tones based on reference images
  // Dark: Deep walnut/ebony - almost black
  // Light: Natural beech/maple - warm beige
  const darkWood = "#2C1810";
  const lightWood = "#D4C5B0";
  const baseColor = $color === 1 ? darkWood : lightWood;

  // Wood grain colors for natural texture
  const grainDark = $color === 1 ? "#1A0F08" : "#B8A890";
  const grainLight = $color === 1 ? "#3E2A1A" : "#E5D9C8";

  return {
    width: $shape === 1 ? "70%" : "70%",
    height: $shape === 1 ? "70%" : "70%",
    borderRadius: $shape === 1 ? "50%" : "15%",
    // Different styles for solid vs hollow (ring)
    background:
      $variant === 0
        ? // Solid: Full wood texture
          `
        radial-gradient(circle at 30% 30%, ${grainLight}40 0%, transparent 50%),
        radial-gradient(circle at 70% 70%, ${grainDark}30 0%, transparent 50%),
        linear-gradient(135deg, 
          ${baseColor} 0%, 
          ${grainLight} 25%,
          ${baseColor} 50%,
          ${grainDark} 75%,
          ${baseColor} 100%)
      `
        : // Hollow: Only border visible (transparent background)
          "transparent",
    // Hollow stones get a thick wooden border
    border:
      $variant === 0
        ? "none"
        : `${$shape === 1 ? "16px" : "14px"} solid ${baseColor}`,
    boxShadow:
      $variant === 0
        ? // Solid: Normal depth
          `
        inset 3px 3px 8px rgba(0,0,0,0.3),
        inset -2px -2px 6px rgba(255,255,255,0.1),
        0 6px 16px rgba(0,0,0,0.4),
        0 2px 4px rgba(0,0,0,0.2)
          `
        : // Hollow: Deep ring shadow on border
          `
        inset 4px 4px 12px rgba(0,0,0,0.7),
        inset -3px -3px 10px rgba(255,255,255,0.05),
        0 6px 16px rgba(0,0,0,0.5),
        0 2px 4px rgba(0,0,0,0.3)
      `,
    position: "relative",
    transition: "all 0.2s ease",
    overflow: $variant === 0 ? "hidden" : "visible",
    // Wood texture and shine overlay
    "&::before":
      $variant === 0
        ? {
            // Solid: Polish shine
            content: '""',
            position: "absolute",
            top: "12%",
            left: "15%",
            width: "35%",
            height: "35%",
            borderRadius: "50%",
            background: `radial-gradient(circle at center, rgba(255,255,255,0.25), rgba(255,255,255,0.08) 45%, transparent 70%)`,
            pointerEvents: "none",
            filter: "blur(3px)",
          }
        : {
            // Hollow: Wood grain texture on border
            content: '""',
            position: "absolute",
            inset: `${$shape === 1 ? "-16px" : "-14px"}`,
            borderRadius: $shape === 1 ? "50%" : "15%",
            background: `
              radial-gradient(circle at 25% 25%, ${grainLight}60 0%, transparent 40%),
              radial-gradient(circle at 75% 75%, ${grainDark}50 0%, transparent 40%),
              linear-gradient(135deg, 
                ${grainLight}40 0%, 
                transparent 30%,
                transparent 70%,
                ${grainDark}40 100%)
            `,
            pointerEvents: "none",
            zIndex: 1,
          },
    // Bottom shadow for depth
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: $variant === 0 ? "10%" : "12%",
      right: $variant === 0 ? "10%" : "12%",
      width: $variant === 0 ? "25%" : "35%",
      height: $variant === 0 ? "25%" : "35%",
      borderRadius: "50%",
      background: `radial-gradient(circle at center, rgba(0,0,0,${
        $variant === 0 ? 0.4 : 0.6
      }), transparent 60%)`,
      pointerEvents: "none",
      filter: "blur(4px)",
    },
  };
});

// Engraved Pattern Component - Like burned/carved patterns on real wood
const EngravedPattern = styled(Box)<{
  $color: number;
  $shape: number;
  $variant: number;
}>(({ theme, $color, $shape, $variant }) => {
  // For hollow stones: light pattern on dark stones, dark pattern on light stones
  const burnColor = $color === 1 ? "#C8B8A0" : "#6B4E3D";

  // Different SVG patterns based on shape and variant
  const getPattern = () => {
    if ($shape === 1 && $variant === 0) {
      // Round + Solid = Star burst pattern
      return `
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="8" fill="${burnColor}" opacity="0.8"/>
          ${Array.from({ length: 8 }, (_, i) => {
            const angle = (i * 45 * Math.PI) / 180;
            const x2 = 50 + Math.cos(angle) * 28;
            const y2 = 50 + Math.sin(angle) * 28;
            return `<line x1="50" y1="50" x2="${x2}" y2="${y2}" stroke="${burnColor}" stroke-width="2.5" opacity="0.75"/>`;
          }).join("")}
          ${Array.from({ length: 8 }, (_, i) => {
            const angle = ((i * 45 + 22.5) * Math.PI) / 180;
            const x2 = 50 + Math.cos(angle) * 20;
            const y2 = 50 + Math.sin(angle) * 20;
            return `<line x1="50" y1="50" x2="${x2}" y2="${y2}" stroke="${burnColor}" stroke-width="1.8" opacity="0.6"/>`;
          }).join("")}
        </svg>
      `;
    } else if ($shape === 1 && $variant === 1) {
      // Round + Hollow = Simple circle on ring
      return `
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="42" fill="none" stroke="${burnColor}" stroke-width="3.5" opacity="0.8"/>
          <circle cx="50" cy="50" r="32" fill="none" stroke="${burnColor}" stroke-width="2.5" opacity="0.6"/>
        </svg>
      `;
    } else if ($shape === 0 && $variant === 0) {
      // Square + Solid = Diamond cross
      return `
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M 50 25 L 75 50 L 50 75 L 25 50 Z" fill="${burnColor}" opacity="0.75"/>
          <line x1="25" y1="50" x2="75" y2="50" stroke="${burnColor}" stroke-width="3" opacity="0.8"/>
          <line x1="50" y1="25" x2="50" y2="75" stroke="${burnColor}" stroke-width="3" opacity="0.8"/>
          <circle cx="50" cy="50" r="6" fill="${burnColor}" opacity="0.85"/>
        </svg>
      `;
    } else {
      // Square + Hollow = Simple square on ring
      return `
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <rect x="15" y="15" width="70" height="70" fill="none" stroke="${burnColor}" stroke-width="3.5" opacity="0.8" rx="8"/>
          <rect x="25" y="25" width="50" height="50" fill="none" stroke="${burnColor}" stroke-width="2.5" opacity="0.6" rx="5"/>
        </svg>
      `;
    }
  };

  return {
    position: "absolute",
    // For hollow (variant 1), pattern fills entire area to be visible on ring
    inset: $variant === 1 ? "-5%" : "15%",
    backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
      getPattern()
    )}")`,
    backgroundSize: $variant === 1 ? "80%" : "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    pointerEvents: "none",
    opacity: $variant === 1 ? 1.0 : 0.9,
    filter: "drop-shadow(0px 1px 1px rgba(0,0,0,0.4))",
    zIndex: 2,
  };
});

function ClassicStone({ stone }: { stone: StoneType }) {
  return (
    <ClassicStoneWrapper $variant={stone.variant || 0} $size={stone.size || 0}>
      <ClassicStoneBase
        $color={stone.color || 0}
        $shape={stone.shape || 0}
        $variant={stone.variant || 0}
      >
        {/* Engraved pattern ONLY on hollow stones (variant = 1) */}
        {stone.variant === 1 && (
          <EngravedPattern
            $color={stone.color || 0}
            $shape={stone.shape || 0}
            $variant={stone.variant || 0}
          />
        )}
      </ClassicStoneBase>
    </ClassicStoneWrapper>
  );
}

// ==================== MODERN THEME ====================
// Light Glass/Chrome: Hell, elegant, futuristisch

const ModernStoneWrapper = styled(Box)<{ $variant: number; $size: number }>(
  ({ theme, $variant, $size }) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    // SIZE: Much more visible difference!
    // Small: 60% size, Large: 130% size
    transform: $size === 1 ? "scale(1.3)" : "scale(0.6)",
  })
);

const ModernStoneBase = styled(Box)<{
  $color: number;
  $shape: number;
  $variant: number;
}>(({ theme, $color, $shape, $variant }) => {
  // ONLY 2 COLORS! Light chrome/silver
  const baseColor = $color === 1 ? "#B0BEC5" : "#ECEFF1"; // Blue-Gray vs Light-Gray
  const highlightColor = $color === 1 ? "#CFD8DC" : "#FFFFFF"; // Lighter highlights

  return {
    width: $shape === 1 ? "68%" : "56%",
    height: $shape === 1 ? "68%" : "56%",
    borderRadius: $shape === 1 ? "50%" : "14%",
    // 3D SPHERE/DIAMOND with glow
    background:
      $variant === 0
        ? $shape === 1
          ? `radial-gradient(circle at 35% 35%, ${highlightColor}FF 0%, ${highlightColor}EE 18%, ${baseColor}FF 50%, ${baseColor}DD 100%)`
          : `linear-gradient(135deg, ${highlightColor}EE 0%, ${baseColor}FF 45%, ${baseColor}EE 55%, ${baseColor}BB 100%)`
        : "transparent",
    border:
      $variant === 0
        ? `2px solid ${highlightColor}AA`
        : `${$shape === 1 ? "14px" : "10px"} solid ${baseColor}EE`,
    backdropFilter: $variant === 0 ? "blur(8px)" : "blur(6px)",
    boxShadow:
      $variant === 0
        ? `
        inset 3px 3px 12px rgba(255,255,255,0.9),
        inset -3px -3px 12px rgba(96,125,139,0.2),
        0 4px 16px rgba(96,125,139,0.2),
        0 2px 8px rgba(96,125,139,0.15)
      `
        : `
        inset 3px 3px 10px rgba(255,255,255,0.6),
        inset -3px -3px 10px rgba(96,125,139,0.3),
        0 4px 16px rgba(96,125,139,0.25),
        0 2px 8px rgba(96,125,139,0.2)
      `,
    position: "relative",
    transition: "all 0.2s ease",
    transform: "translateZ(0)",
    // Enhanced 3D glow highlight
    "&::before":
      $variant === 0
        ? {
            content: '""',
            position: "absolute",
            top: $shape === 1 ? "15%" : "12%",
            left: $shape === 1 ? "22%" : "18%",
            width: $shape === 1 ? "32%" : "28%",
            height: $shape === 1 ? "32%" : "28%",
            borderRadius: $shape === 1 ? "50%" : "35%",
            background:
              $shape === 1
                ? `radial-gradient(circle at center, rgba(255,255,255,0.9), rgba(255,255,255,0.5) 35%, transparent 60%)`
                : `radial-gradient(circle at center, rgba(255,255,255,0.8), rgba(255,255,255,0.3) 45%, transparent 65%)`,
            pointerEvents: "none",
            filter: "blur(5px)",
          }
        : {},
    // Subtle depth shadow
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: $variant === 0 ? "8%" : "12%",
      right: $variant === 0 ? "12%" : "15%",
      width: $variant === 0 ? "35%" : "28%",
      height: $variant === 0 ? "35%" : "28%",
      borderRadius: $shape === 1 ? "50%" : "20%",
      background: `radial-gradient(circle at center, rgba(96,125,139,0.25), transparent 60%)`,
      pointerEvents: "none",
      filter: "blur(5px)",
    },
  };
});

function ModernStone({ stone }: { stone: StoneType }) {
  return (
    <ModernStoneWrapper $variant={stone.variant || 0} $size={stone.size || 0}>
      <ModernStoneBase
        $color={stone.color || 0}
        $shape={stone.shape || 0}
        $variant={stone.variant || 0}
      />
    </ModernStoneWrapper>
  );
}

// ==================== DARK THEME ====================
// Dark Cyberpunk/Neon: Dunkel, cyan/pink neon, futuristisch

const DarkStoneWrapper = styled(Box)<{ $variant: number; $size: number }>(
  ({ theme, $variant, $size }) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    // SIZE: Much more visible difference!
    // Small: 60% size, Large: 130% size
    transform: $size === 1 ? "scale(1.3)" : "scale(0.6)",
  })
);

const DarkStoneBase = styled(Box)<{
  $color: number;
  $shape: number;
  $variant: number;
}>(({ theme, $color, $shape, $variant }) => {
  // ONLY 2 COLORS! Cyan neon and Pink neon
  const baseColor = $color === 1 ? "#00BCD4" : "#FF4081"; // Cyan vs Pink
  const highlightColor = $color === 1 ? "#00E5FF" : "#FF80AB"; // Brighter neon

  return {
    width: $shape === 1 ? "68%" : "58%",
    height: $shape === 1 ? "68%" : "58%",
    borderRadius: $shape === 1 ? "50%" : "12%",
    // 3D SPHERE/DIAMOND with intense neon glow
    background:
      $variant === 0
        ? $shape === 1
          ? `radial-gradient(circle at 35% 35%, ${highlightColor}FF 0%, ${highlightColor}EE 20%, ${baseColor}FF 52%, ${baseColor}DD 100%)`
          : `linear-gradient(135deg, ${highlightColor}EE 0%, ${baseColor}FF 46%, ${baseColor}EE 54%, ${baseColor}BB 100%)`
        : "transparent",
    border:
      $variant === 0
        ? "none"
        : `${$shape === 1 ? "13px" : "9px"} solid ${baseColor}EE`,
    boxShadow:
      $variant === 0
        ? $shape === 1
          ? `
            inset 4px 4px 18px rgba(0,0,0,0.6),
            inset -4px -4px 16px rgba(255,255,255,0.15),
            0 0 45px ${baseColor}AA,
            0 0 75px ${baseColor}66,
            0 10px 36px rgba(0,0,0,0.8)
          `
          : `
            inset 3px 3px 14px rgba(255,255,255,0.2),
            inset -3px -3px 14px rgba(0,0,0,0.7),
            0 0 40px ${baseColor}99,
            0 0 65px ${baseColor}55,
            0 8px 28px rgba(0,0,0,0.7)
          `
        : `
        inset 4px 4px 14px rgba(0,0,0,0.8),
        inset -3px -3px 12px rgba(255,255,255,0.1),
        0 0 40px ${baseColor}88,
        0 0 65px ${baseColor}55,
        0 8px 28px rgba(0,0,0,0.7)
      `,
    position: "relative",
    transition: "all 0.2s ease",
    transform: "translateZ(0)",
    // Enhanced neon glow highlight
    "&::before":
      $variant === 0
        ? {
            content: '""',
            position: "absolute",
            top: $shape === 1 ? "14%" : "11%",
            left: $shape === 1 ? "21%" : "17%",
            width: $shape === 1 ? "34%" : "30%",
            height: $shape === 1 ? "34%" : "30%",
            borderRadius: $shape === 1 ? "50%" : "32%",
            background:
              $shape === 1
                ? `radial-gradient(circle at center, rgba(255,255,255,0.95), rgba(255,255,255,0.6) 35%, transparent 60%)`
                : `radial-gradient(circle at center, rgba(255,255,255,0.85), rgba(255,255,255,0.4) 45%, transparent 65%)`,
            pointerEvents: "none",
            filter: "blur(6px)",
          }
        : {},
    // Deep dark shadow for neon contrast
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: $variant === 0 ? "6%" : "10%",
      right: $variant === 0 ? "8%" : "12%",
      width: $variant === 0 ? "38%" : "32%",
      height: $variant === 0 ? "38%" : "32%",
      borderRadius: $shape === 1 ? "50%" : "25%",
      background: `radial-gradient(circle at center, rgba(0,0,0,0.8), transparent 65%)`,
      pointerEvents: "none",
      filter: "blur(8px)",
    },
  };
});

function DarkStone({ stone }: { stone: StoneType }) {
  return (
    <DarkStoneWrapper $variant={stone.variant || 0} $size={stone.size || 0}>
      <DarkStoneBase
        $color={stone.color || 0}
        $shape={stone.shape || 0}
        $variant={stone.variant || 0}
      />
    </DarkStoneWrapper>
  );
}

// ==================== EXPORT ====================

export function DefaultStone({ stone, themeSet }: DefaultStoneProps) {
  switch (themeSet) {
    case "classic":
      return <ClassicStone stone={stone} />;
    case "modern":
      return <ModernStone stone={stone} />;
    case "dark":
      return <DarkStone stone={stone} />;
    default:
      return <ClassicStone stone={stone} />;
  }
}
